import {
    CheckCircleFilled,
    CloseCircleFilled,
    DatabaseOutlined,
    DownloadOutlined,
    ExclamationCircleOutlined,
    FileExcelOutlined,
    InboxOutlined,
    LoadingOutlined,
    RocketOutlined,
    SyncOutlined,
    ThunderboltOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import {
    Alert,
    Button,
    Card,
    Col,
    Progress,
    Row,
    Space,
    Statistic,
    Steps,
    Table,
    Tag,
    Typography,
} from "antd";
import Dragger from "antd/es/upload/Dragger";
import * as XLSX from "xlsx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import PageLayout from "../../components/PageLayout";
import axiosInstance from "../../variables/api/axiosInstance";
import axiosMultiPartDataInstance from "../../variables/api/axiosMultiPartDatainstance";

const { Title, Text } = Typography;

const POLL_INTERVAL_MS = 2500;
const ETAT_LABELS = { EN_COURS: "En cours", TERMINE: "Terminé", ERREUR: "Erreur", INCONNU: "Inconnu" };

export default function RepriseGumVtReglements() {
    const [step, setStep]                 = useState(0);
    const [fichier, setFichier]           = useState(null);
    const [rapport, setRapport]           = useState(null);
    const [loading, setLoading]           = useState(false);
    const [progress, setProgress]         = useState(0);
    const [jobId, setJobId]               = useState(null);
    const [jobEtat, setJobEtat]           = useState(null);
    const pollRef = useRef(null);

    useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

    const lancer = async () => {
        if (!fichier) { toast.warning("Sélectionnez le fichier Excel."); return; }
        setLoading(true);
        setRapport(null);
        setProgress(5);
        setJobEtat("EN_COURS");
        setStep(1);
        try {
            const form = new FormData();
            form.append("fichier", fichier);
            const res = await axiosMultiPartDataInstance.post(
                "/api/migration/reprise-gumvt/completer-reglements", form,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            const id = res.data?.jobId;
            if (!id) throw new Error("Aucun jobId reçu du serveur.");
            setJobId(id);
            demarrerPolling(id);
        } catch (err) {
            setLoading(false);
            setJobEtat("ERREUR");
            toast.error("Erreur : " + (err.response?.data?.message || err.message));
        }
    };

    const demarrerPolling = useCallback((id) => {
        let ticks = 0;
        pollRef.current = setInterval(async () => {
            ticks++;
            setProgress(p => Math.min(p + Math.max(1, 5 - Math.floor(ticks / 5)), 90));
            try {
                const res = await axiosInstance.get(`/api/migration/job/${id}`);
                const statut = res.data;
                if (statut.etat === "TERMINE" || statut.etat === "ERREUR") {
                    clearInterval(pollRef.current);
                    setProgress(100);
                    setJobEtat(statut.etat);
                    setLoading(false);
                    if (statut.etat === "TERMINE") {
                        setRapport(statut.resultat);
                        setStep(2);
                        toast.success("Complétion des règlements terminée !");
                    } else {
                        toast.error("Erreur : " + (statut.erreur || "Erreur inconnue"));
                    }
                }
            } catch (err) {
                clearInterval(pollRef.current);
                setLoading(false);
                setJobEtat("ERREUR");
                toast.error("Erreur polling : " + err.message);
            }
        }, POLL_INTERVAL_MS);
    }, []);

    const exportRapport = () => {
        if (!rapport) return;
        const wb = XLSX.utils.book_new();
        const resume = [
            ["Indicateur", "Valeur"],
            ["Règlements parsés", rapport.totalParse],
            ["Créés", rapport.succes],
            ["Déjà existants", rapport.dejaExistants],
            ["Erreurs", rapport.erreurs],
            ["Durée (ms)", rapport.dureeMs],
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(resume), "Résumé");
        const errData = [["Détail"], ...(rapport.lignesErreur || []).map(e => [e])];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(errData), "Erreurs");
        XLSX.writeFile(wb, `completer_reglements_gumvt_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const reinitialiser = () => {
        if (pollRef.current) clearInterval(pollRef.current);
        setStep(0); setFichier(null); setRapport(null);
        setLoading(false); setProgress(0); setJobId(null); setJobEtat(null);
    };

    return (
        <PageLayout currentMenu="REPRISE">
            <div style={{ padding: "24px 0" }}>
                <Space align="center" style={{ marginBottom: 24 }}>
                    <DatabaseOutlined style={{ fontSize: 28, color: "#242166" }} />
                    <div>
                        <Title level={4} style={{ margin: 0, color: "#242166" }}>
                            Compléter les règlements — GUM / VT
                        </Title>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Ajoute les règlements manquants aux sinistres déjà importés (non destructif)
                        </Text>
                    </div>
                </Space>

                <Steps
                    current={step}
                    style={{ marginBottom: 32 }}
                    items={[
                        { title: "Sélection", description: "Choisir le fichier Excel" },
                        { title: "Traitement", description: "Lecture feuille Réglements" },
                        { title: "Rapport", description: "Résultats" },
                    ]}
                />

                {step === 0 && (
                    <Card>
                        <Alert
                            type="info" showIcon style={{ marginBottom: 16 }}
                            message="Cette opération lit uniquement la feuille « Réglements » et crée les décaissements manquants pour les sinistres GUM/VT déjà présents en base. Elle ne recrée ni sinistres, ni évaluations, ni recours."
                        />
                        <Dragger
                            accept=".xlsx,.xls"
                            multiple={false}
                            beforeUpload={(f) => { setFichier(f); return false; }}
                            onRemove={() => setFichier(null)}
                            maxCount={1}
                            style={{ marginBottom: 24 }}
                        >
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined style={{ color: "#242166" }} />
                            </p>
                            <p className="ant-upload-text">
                                Cliquez ou déposez le fichier Excel Pool GUM/VT ici
                            </p>
                            <p className="ant-upload-hint">
                                Le même fichier que l'import — seule la feuille « Réglements » est utilisée
                            </p>
                        </Dragger>

                        {fichier && (
                            <Alert
                                type="success" showIcon
                                message={
                                    <Space>
                                        <FileExcelOutlined style={{ color: "#217346" }} />
                                        <Text strong>{fichier.name}</Text>
                                        <Text type="secondary">({(fichier.size / 1024).toFixed(1)} Ko)</Text>
                                    </Space>
                                }
                                style={{ marginBottom: 16 }}
                            />
                        )}

                        <div style={{ textAlign: "right" }}>
                            <Button
                                type="primary" size="large"
                                icon={<ThunderboltOutlined />}
                                disabled={!fichier}
                                onClick={lancer}
                                style={{ background: "#242166" }}
                            >
                                Lancer la complétion
                            </Button>
                        </div>
                    </Card>
                )}

                {step === 1 && (
                    <Card>
                        <div style={{ textAlign: "center", padding: "40px 0" }}>
                            {loading ? (
                                <>
                                    <LoadingOutlined style={{ fontSize: 48, color: "#242166", marginBottom: 20 }} />
                                    <div style={{ marginBottom: 8 }}>
                                        <Text strong style={{ fontSize: 16 }}>Complétion des règlements en cours…</Text>
                                    </div>
                                    {jobId && (
                                        <div style={{ marginBottom: 12 }}>
                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                Job ID : <Text code>{jobId}</Text>
                                            </Text>
                                            <Tag icon={<SyncOutlined spin />} color="processing" style={{ marginLeft: 8 }}>
                                                {ETAT_LABELS[jobEtat] || jobEtat}
                                            </Tag>
                                        </div>
                                    )}
                                    <Progress percent={progress} strokeColor="#242166" style={{ maxWidth: 500, margin: "0 auto" }} />
                                </>
                            ) : jobEtat === "ERREUR" ? (
                                <>
                                    <CloseCircleFilled style={{ fontSize: 48, color: "#ff4d4f", marginBottom: 20 }} />
                                    <div><Text type="danger" strong style={{ fontSize: 16 }}>L'opération a échoué.</Text></div>
                                    <Button onClick={reinitialiser} style={{ marginTop: 16 }}>Recommencer</Button>
                                </>
                            ) : null}
                        </div>
                    </Card>
                )}

                {step === 2 && rapport && (
                    <div>
                        <Alert
                            type={rapport.ok ? "success" : "warning"}
                            showIcon
                            icon={rapport.ok ? <CheckCircleFilled style={{ color: "#52c41a" }} />
                                             : <ExclamationCircleOutlined style={{ color: "#faad14" }} />}
                            message={rapport.ok ? "Complétion terminée avec succès"
                                                : `Complétion terminée avec ${rapport.erreurs} erreur(s)`}
                            description={`Durée : ${(rapport.dureeMs / 1000).toFixed(1)}s`}
                            style={{ marginBottom: 20 }}
                        />

                        <Card style={{ marginBottom: 20 }}>
                            <Row gutter={[16, 16]}>
                                <Col xs={12} sm={6}>
                                    <Statistic title="Règlements parsés" value={rapport.totalParse}
                                        valueStyle={{ fontSize: 20 }} />
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Statistic title="Créés" value={rapport.succes}
                                        valueStyle={{ color: "#52c41a", fontSize: 20 }}
                                        prefix={<CheckCircleFilled />} />
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Statistic title="Déjà existants" value={rapport.dejaExistants}
                                        valueStyle={{ color: "#8c8c8c", fontSize: 20 }} />
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Statistic title="Erreurs" value={rapport.erreurs}
                                        valueStyle={{ color: rapport.erreurs > 0 ? "#ff4d4f" : "#52c41a", fontSize: 20 }} />
                                </Col>
                            </Row>
                        </Card>

                        {rapport.lignesErreur?.length > 0 && (
                            <Card
                                size="small"
                                title={
                                    <Space>
                                        <WarningOutlined style={{ color: "#ff4d4f" }} />
                                        <Text strong type="danger">
                                            Sinistres introuvables / erreurs ({rapport.lignesErreur.length})
                                        </Text>
                                    </Space>
                                }
                                style={{ marginBottom: 20 }}
                            >
                                <Table
                                    dataSource={rapport.lignesErreur.map((msg, i) => ({ key: i, ligne: i + 1, message: msg }))}
                                    columns={[
                                        { title: "#", dataIndex: "ligne", key: "ligne", width: 50,
                                          render: v => <Text type="secondary" style={{ fontSize: 11 }}>{v}</Text> },
                                        { title: "Détail", dataIndex: "message", key: "message",
                                          render: v => <Text type="danger" style={{ fontSize: 12 }}>{v}</Text> },
                                    ]}
                                    size="small"
                                    pagination={{ pageSize: 20, size: "small" }}
                                    scroll={{ y: 300 }}
                                />
                            </Card>
                        )}

                        <Space>
                            <Button icon={<DownloadOutlined />} onClick={exportRapport}>
                                Exporter rapport Excel
                            </Button>
                            <Button type="primary" icon={<RocketOutlined />} onClick={reinitialiser}
                                style={{ background: "#242166" }}>
                                Nouvelle complétion
                            </Button>
                        </Space>
                    </div>
                )}
            </div>
        </PageLayout>
    );
}
